import { z } from "zod";

/**
 * Good faith exams (GFE): a client needs a current one for services marked "Needs a good faith exam".
 * Current = a Qualiphy approval, or a tag that counts as one (e.g. "MD Approved"), younger than the
 * clinic's approval period on the visit date.
 */
export type GfeState =
  | "current" // approved (Qualiphy or medical director), good on the visit date
  | "current_flagged" // approved with restrictions staff flagged
  | "review" // approved with a provider note nobody has read yet
  | "intake" // set up; goes out once the client fills in the intake (it pre-fills the exam)
  | "pending" // sent; waiting for the client or the provider
  | "deferred" // Qualiphy's provider sent it to their medical director
  | "declined" // Qualiphy rejected it
  | "not_done" // NA or missed: resend
  | "needed"; // nothing current and nothing waiting

export interface GfeSummary {
  state: GfeState;
  /** The Qualiphy exam this is for (the service's, or the clinic's default). */
  qualiphyExamId: number | null;
  /** Good until (date), when current. */
  until: string | null;
  source: "qualiphy" | "tag" | null;
  /** The exam this is about, if any (for resend, cancel, review). */
  examId: string | null;
  /** Staff's restriction note when flagged. */
  flagNote: string | null;
}

export const GFE_LABELS: Record<GfeState, string> = {
  current: "GFE ✓",
  current_flagged: "GFE ✓ with restrictions",
  review: "GFE ✓, review notes",
  intake: "GFE after forms",
  pending: "GFE pending",
  deferred: "GFE pending (medical director)",
  declined: "GFE declined",
  not_done: "GFE not done",
  needed: "GFE needed",
};

/** States where the visit can go ahead without a caution. */
export const gfeOk = (s: GfeState) => s === "current" || s === "current_flagged" || s === "review";

export interface QualiphyExamView {
  id: string;
  /** Qualiphy's exam (e.g. 4623). */
  qualiphyExamId: number | null;
  appointmentId: string | null;
  status: "waiting" | "sending" | "sent" | "completed" | "cancelled" | "failed";
  result: "approved" | "rejected" | "deferred" | "na" | "missed" | null;
  reason: string | null;
  providerName: string | null;
  /** The state the call was sent for ("OK"; "TE" in test mode). */
  teleState: string | null;
  sentAt: string;
  resultAt: string | null;
  goodUntil: string | null;
  hasPdf: boolean;
  review: "none" | "needed" | "flagged" | "cleared";
  flagNote: string | null;
  test: boolean;
  via: "auto" | "staff";
  sentBy: string | null;
  error: string | null;
  /** Why the intake's answers didn't reach Qualiphy. */
  answersError: string | null;
  /** When we sent the Qualiphy link itself (fallback, "do it later", or a resend). */
  linkSentAt: string | null;
  questionsAnswers: { no: number; question: string; answer: string }[];
}

/** Without an exam: the visit's service's exam, else the clinic's default. */
export const gfeSendSchema = z.object({ appointmentId: z.string().uuid().nullable().default(null), qualiphyExamId: z.number().int().positive().nullable().default(null) });
export const gfeReviewSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("flag"), note: z.string().trim().min(1).max(500) }),
  z.object({ action: z.literal("clear") }),
]);

/** Qualiphy's exam catalog entry, for settings. */
export interface QualiphyCatalogExam {
  id: number;
  title: string;
  rxType: number;
}

export interface GfeSettingsView {
  /** A Qualiphy key is set up for this clinic. */
  connected: boolean;
  /** Why not, when it isn't. */
  problem: string | null;
  exams: QualiphyCatalogExam[];
  production: boolean;
  /** This clinic's private webhook address (also set it in Qualiphy's portal for exams created there). */
  webhookUrl: string | null;
}

export interface GfeSwitchView {
  enabled: boolean;
  pausedUntil: string | null;
}
